/* eslint-disable react/no-unstable-nested-components */
import React, { Dispatch, FC, JSX, SetStateAction, useEffect, useMemo, useRef, useState } from "react";

import { arrayToCsvFromCharts, downloadBlob } from "components/dashboard/product-template-table/utils/utils";
import {
  AnyFilterExpressionInput,
  SortInput,
  TablesDetailsInput,
  useUpdateTableDetailsForUserMutation
} from "gql/generated/types";
import { useTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { T } from "services/lang/LanguageService";
import { CSV_TYPE } from "utils/constants";
import { exportExcel, moveItem } from "utils/utils";
import useProjectContext from "../hooks/useProjectContext";
import useUserTableDetails from "../hooks/useUserTableDetails";
import { LoadingStates, MdmContainerName, MdmTableName, TableColumn, TableOptions } from "../utils/types";
import { errorToaster, fetchDataHelper, findObjectValue, formatSort, tryFormatDateString } from "../utils/utils";
import TableFilters from "./TableFilters";

type LazyTableProps<T> = {
  data: T[];
  tableName: MdmTableName;
  containerName: MdmContainerName;
  columns: TableColumn<T>[];
  loading: boolean;
  setTableOptions: Dispatch<SetStateAction<TableOptions>>;
  tableOptions: TableOptions;
  totalItems: number;
  fetchData: (
    take?: number | null,
    skip?: number | null,
    sort?: SortInput[] | null,
    filters?: AnyFilterExpressionInput[] | null,
    cacheAndNetwork?: boolean,
    filter?: AnyFilterExpressionInput | null
  ) => Promise<any>;
  resetFilters: () => void;
  buttons?: JSX.Element;
  centerTools?: JSX.Element;
  onSearch?: (value: string) => void;
  setCustomColumns: Dispatch<SetStateAction<string[]>>;
  formatExportData?: (data: T, field: string) => string | null;
};

const LazyTable = <T,>({
  data,
  tableName,
  containerName,
  columns,
  loading,
  setTableOptions,
  tableOptions,
  totalItems,
  fetchData,
  resetFilters,
  buttons,
  centerTools,
  onSearch,
  setCustomColumns,
  formatExportData,
}: LazyTableProps<T>) => {
  const tz = useTimezone();
  const [viewFilters, setViewFilters] = useState(false);
  const [filtered, setFiltered] = useState(false);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({ excel: false, csv: false });
  const [page, setPage] = useState(tableOptions.page ?? 0);
  const dt = useRef<DataTable<any>>(null);
  const { userSettings, project } = useProjectContext();
  const tableDetails = useUserTableDetails(userSettings, tableName);

  const [updateTableDetails] = useUpdateTableDetailsForUserMutation();

  useEffect(() => {
    if (tableOptions.andFilters.length > 0) {
      setViewFilters(true);
      setFiltered(true);
    } else {
      setFiltered(false);
    }
  }, [tableOptions]);

  const totalPage = useMemo(
    () => Math.ceil(totalItems / tableOptions.rows),
    [totalItems, tableOptions.rows]
  );

  const handleExport = async ({ excel = false, csv = false }: LoadingStates) => {
    setLoadingStates(prev => ({ ...prev, excel, csv }));
    const sort = formatSort(
      tableOptions.sortField,
      tableOptions.sortOrder,
      tableName,
      project?.mdmProjectById.configuration
    );
    try {
      const responseItems = await fetchDataHelper<T>(totalItems, sort, fetchData);
      if (responseItems.length > 0) {
        const tableData = responseItems.map(row => {
          const newData: Record<string, unknown> = {};
          columns.forEach(col => {
            if (col.hideInFile || col.hidden) return;
            const value = formatExportData?.(row, col.field!);
            if (value) {
              newData[col.header] = value;
            } else {
              newData[col.header] = col.field
                ? tryFormatDateString(findObjectValue(row, col.field)?.toString(), tz.display)
                : col.body;
            }
          });
          return newData;
        });
        if (excel) exportExcel(tableData, tableName);
        if (csv) downloadBlob(arrayToCsvFromCharts(tableData), `${tableName}.csv`, CSV_TYPE);
      }
    } catch (error) {
      console.error(error);
      errorToaster(T("error.something-went-wrong"));
    } finally {
      setLoadingStates(prev => ({ ...prev, excel: false, csv: false }));
    }
  };

  const onPage = (e: DataTableStateEvent) => {
    const tableDetailsInput: TablesDetailsInput = {
      table: tableName,
      rows: e.rows,
      columnOrders: tableDetails?.columnOrders || [],
    };
    if (tableDetails?.rows !== e.rows) {
      updateTableDetails({
        variables: {
          projectId: project?.mdmProjectById.id!,
          tableDetails: tableDetailsInput,
        },
      });
    }
    setTableOptions(prev => ({ ...prev, first: e.first, page: e.page, rows: e.rows }));
  };

  const onSort = (e: DataTableStateEvent) => {
    const sort = formatSort(
      e.sortField,
      e.sortOrder,
      tableName,
      project?.mdmProjectById.configuration
    );
    setTableOptions(prev => ({
      ...prev,
      sortField: e.sortField,
      sortOrder: e.sortOrder,
      first: 0,
      reqSortField: sort[0].property,
    }));
  };

  const paginatorTemplate = useMemo(
    () => ({
      layout:
        "RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
      CurrentPageReport: () => {
        return (
          <div
            className="ml-3 flex gap-3 align-items-center w-20rem"
            style={{ color: "var(--text-color)", userSelect: "none" }}
          >
            <div>{`${T("word.total")} ${totalItems}`}</div>
            <div className="p-inputgroup w-7rem">
              <InputNumber
                min={1}
                useGrouping={false}
                className="flex-1"
                title={`${T("word.total")} ${totalPage} ${T("word.pages")}`}
                value={(tableOptions.page ?? 0) + 1}
                onKeyDown={e =>
                  e.key === "Enter" &&
                  page + 1 <= totalPage &&
                  setTableOptions(prev => ({
                    ...prev,
                    page,
                    first: page * prev.rows,
                  }))
                }
                onChange={e => {
                  if (!e.value) return;
                  setPage(e.value - 1);
                }}
              />
              <span className="p-inputgroup-addon">{` / ${totalPage}`}</span>
            </div>
          </div>
        );
      },
    }),
    [page, setTableOptions, tableOptions.page, totalItems, totalPage]
  );

  return (
    <div className="card p-0 sm:p-3">
      <Toolbar
        start={
          <div className="flex gap-3 align-items-center">
            {onSearch && <SearchInput onSearch={onSearch} tableOptions={tableOptions} />}
            <Button
              title={T("word.filters")}
              type="button"
              size="small"
              icon={filtered ? "pi pi-filter-fill" : "pi pi-filter"}
              className="p-button-outlined"
              onClick={() => setViewFilters(prev => !prev)}
            />
            <Button
              title={T("action.clear-filters")}
              icon="pi pi-filter-slash"
              size="small"
              className="p-button-outlined p-button-danger"
              onClick={resetFilters}
            />
          </div>
        }
        center={<div className="flex gap-3">{centerTools}</div>}
        end={
          <div className="flex gap-3">
            <Button
              type="button"
              icon="pi pi-file"
              className="p-button-outlined p-button-warning"
              title={T("action.export-to-csv")}
              size="small"
              loading={loadingStates.csv}
              onClick={() => handleExport({ csv: true })}
            />
            <Button
              type="button"
              icon="pi pi-file-excel"
              className="p-button-outlined p-button-warning"
              title={T("action.export-to-excel")}
              size="small"
              loading={loadingStates.excel}
              onClick={() => handleExport({ excel: true })}
            />
            {buttons}
          </div>
        }
        className="gap-3"
      />

      <DataTable
        header={
          <div
            className={`${viewFilters ? "max-h-90rem" : "max-h-0"} overflow-hidden transition-all transition-duration-500 p-0`}
          >
            <TableFilters
              container={containerName}
              tableName={tableName}
              tableOptions={tableOptions}
              setTableOptions={setTableOptions}
            />
          </div>
        }
        ref={dt}
        value={data}
        stripedRows
        paginator
        rows={tableOptions.rows}
        reorderableColumns
        onColReorder={e => setCustomColumns(prev => moveItem(prev, e.dragIndex, e.dropIndex))}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        paginatorTemplate={paginatorTemplate}
        loading={loading}
        size="small"
        totalRecords={totalItems}
        lazy
        className="overflow-x-auto lazy-table"
        first={tableOptions.first || 0}
        sortField={tableOptions.sortField}
        sortOrder={tableOptions.sortOrder}
        onPage={onPage}
        onSort={onSort}
      >
        {columns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </div>
  );
};

const SearchInput: FC<{
  onSearch: (value: string) => void;
  tableOptions: TableOptions;
}> = ({ onSearch, tableOptions }) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch(tableOptions.search);
  }, [tableOptions]);

  return (
    <div className="p-inputgroup align-items-center">
      <InputText
        value={search ?? ""}
        onChange={e => setSearch(e.target.value)}
        placeholder={`${T("action.search")}...`}
        onKeyUp={e => e.key === "Enter" && onSearch(search)}
/>
      <Button
        className="p-inputgroup-addon"
        size="small"
        icon="pi pi-search"
        onClick={() => onSearch(search)}
      />
    </div>
  );
};

export default LazyTable;
